import React, { useRef, useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import NotificationItem from './NotificationItem';
import { PARTNERSHIP_TYPES, MILESTONE_TYPES } from '../../hooks/useNotifications';

const {
  FiBell, FiCheck, FiLoader, FiInbox,
  FiHandshake, FiTarget, FiZap, FiFilter,
} = FiIcons;

const FILTERS = [
  { id: 'all',          label: 'All',          icon: FiBell },
  { id: 'unread',       label: 'Unread',       icon: FiZap },
  { id: 'partnerships', label: 'Partnerships', icon: FiHandshake },
  { id: 'milestones',   label: 'Milestones',   icon: FiTarget },
];

const TYPE_MAP = {
  partnerships: PARTNERSHIP_TYPES.filter(t => !MILESTONE_TYPES.includes(t)),
  milestones:   MILESTONE_TYPES,
};

/* ── Date group header ── */
const dateGroup = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return `${diff} days ago`;
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
};

const groupByDate = (items) => {
  const groups = {};
  items.forEach(n => {
    const g = dateGroup(n.created_at);
    if (!groups[g]) groups[g] = [];
    groups[g].push(n);
  });
  return groups;
};

const NotificationPanel = ({
  notifications, unreadCount, loading,
  onMarkRead, onMarkAllRead, onDelete, onClose,
}) => {
  const ref = useRef(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  const filtered = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (TYPE_MAP[filter]) return TYPE_MAP[filter].includes(n.type);
    return true;
  });

  const grouped = groupByDate(filtered);

  const partnershipUnread = notifications.filter(
    n => !n.is_read && PARTNERSHIP_TYPES.filter(t => !MILESTONE_TYPES.includes(t)).includes(n.type)
  ).length;
  const milestoneUnread = notifications.filter(
    n => !n.is_read && MILESTONE_TYPES.includes(n.type)
  ).length;

  const badgeFor = (id) => {
    if (id === 'unread') return unreadCount;
    if (id === 'partnerships') return partnershipUnread;
    if (id === 'milestones') return milestoneUnread;
    return null;
  };

  return (
    <div ref={ref}
      className="absolute right-0 top-full mt-3 w-[400px] z-[200] overflow-hidden rounded-3xl"
      style={{
        background: 'linear-gradient(160deg, rgba(25,16,38,0.98) 0%, rgba(15,10,25,0.99) 100%)',
        border: '1px solid rgba(255,102,204,0.12)',
        boxShadow: '0 32px 80px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)',
        backdropFilter: 'blur(40px)',
      }}>

      {/* ── Header ── */}
      <div className="px-5 pt-5 pb-4"
        style={{ background: 'linear-gradient(90deg, rgba(255,102,204,0.07) 0%, rgba(255,102,0,0.04) 100%)' }}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-pink/20 to-brand-coral/10 border border-brand-pink/20 flex items-center justify-center">
              <SafeIcon icon={FiBell} className="text-brand-pink text-sm" />
            </div>
            <div>
              <p className="text-sm font-black uppercase tracking-widest text-white leading-none">Notifications</p>
              {unreadCount > 0 && (
                <p className="text-[9px] font-black text-brand-pink/70 uppercase tracking-widest mt-0.5">{unreadCount} unread</p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <button onClick={onMarkAllRead}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-white/8 text-[9px] font-black uppercase tracking-widest text-zinc-500 hover:text-white hover:border-white/15 transition-all">
              <SafeIcon icon={FiCheck} className="text-[9px]" /> All read
            </button>
          )}
        </div>

        {/* Filter pills */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5">
          {FILTERS.map(f => {
            const badge = badgeFor(f.id);
            return (
              <button key={f.id} onClick={() => setFilter(f.id)}
                className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest whitespace-nowrap transition-all flex-shrink-0 ${
                  filter === f.id
                    ? 'bg-gradient-to-r from-brand-pink to-brand-coral text-white shadow-[0_0_10px_rgba(255,102,204,0.3)]'
                    : 'border border-white/8 text-zinc-600 hover:text-zinc-300 hover:border-white/15'
                }`}>
                <SafeIcon icon={f.icon} className="text-[9px]" />
                {f.label}
                {badge > 0 && (
                  <span className={`min-w-[16px] h-4 px-1 rounded-full text-[8px] font-black flex items-center justify-center ${
                    filter === f.id ? 'bg-white/20 text-white' : 'bg-brand-pink text-white'
                  }`}>
                    {badge > 9 ? '9+' : badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Notification list ── */}
      <div className="max-h-[440px] overflow-y-auto px-3 pb-3">
        {loading ? (
          <div className="flex flex-col items-center gap-3 py-14">
            <SafeIcon icon={FiLoader} className="text-brand-pink/40 text-2xl animate-spin" />
            <p className="text-[9px] font-black uppercase tracking-widest text-zinc-700">Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-14 text-center">
            <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/8 flex items-center justify-center">
              <SafeIcon icon={FiInbox} className="text-zinc-700 text-xl" />
            </div>
            <p className="text-[9px] font-black uppercase tracking-widest text-zinc-700">No notifications</p>
          </div>
        ) : (
          Object.entries(grouped).map(([group, items]) => (
            <div key={group}>
              <div className="px-2 py-2 mt-2">
                <span className="text-[9px] font-black uppercase tracking-widest text-zinc-700">{group}</span>
              </div>
              <div className="space-y-1">
                {items.map(n => (
                  <NotificationItem key={n.id} notification={n}
                    onMarkRead={onMarkRead} onDelete={onDelete} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* ── Footer ── */}
      {notifications.length > 0 && (
        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between">
          <p className="text-[9px] font-black uppercase tracking-widest text-zinc-700">
            {notifications.length} total
          </p>
          <p className="text-[9px] font-black uppercase tracking-widest text-zinc-700">
            {unreadCount} unread
          </p>
        </div>
      )}
    </div>
  );
};

export default NotificationPanel;